/* eslint-disable no-console */
import Vue from 'vue'
import { DialogProgrammatic as Dialog } from 'buefy'
import { snakeCase, camelCase } from 'lodash'
import { getGraphql } from '../static/graphql/getGraphql.js'
import { getGeo } from '@/utils/address.js'

const consoleLogHeader = (s) => {
  console.log(`\n\n===== ${s} ====================\n`)
}

const consoleLogHeaderItem = (s) => {
  console.log(`-- ${s}\n`)
}

/*
convert a given field name into profile attribute name (handles smart survey fields)

Examples:
  firstName -> firstName
  first_name:e541153e0b9e54eca4809a73f3cd6fff -> firstName
  ethnicity:e541153e0b9e54eca4809a73f3cd6eee -> ethnicity
*/
const getProfileAttributeFromField = (field) => camelCase(field.split(':')[0])

export const state = () => ({
  username: '',
  schema: undefined,
  currentStepNum: 0,
  currentStepName: '',
  application: [],
  loading: false,
  verifications: [],
  cleanApplication: undefined,
  partnerHistory: [],
  env: process.env.DEV_ENV,
  berbix: {
    ClientToken: undefined,
    Transaction: undefined,
    finished: false,
  },
  userPaymentOptions: [],
  userVendorConnections: [],
  windowHeight: window.innerHeight,
  windowWidth: window.innerWidth,
  uploadHelperDataMap: new Map(),
  cookies: {},
  allowedVerifications: undefined,
  modalState: false,
  complianceRequirements: null,
  featureFlag: {},
})

export const mutations = {
  reset(state) {
    state.currentStepNum = 0
    state.application = []
    state.loading = false
    state.cleanApplication = undefined
    state.verifications = []
    state.berbix = {
      ClientToken: undefined,
      Transaction: undefined,
      finished: false,
    }
    state.userPaymentOptions = []
  },
  setWindowSize(state, w) {
    state.windowHeight = w.innerHeight
    state.windowWidth = w.innerWidth
  },
  setberbixFinished(state) {
    state.berbix.finished = true
  },
  setBerbix(state, payload) {
    state.berbix = payload
  },
  partnerHistory(state, partner) {
    state.partnerHistory.unshift(partner)
  },
  setLoading(state, payload) {
    state.loading = payload
  },
  setPartnerSchema(state, payload) {
    state.schema = payload
  },
  setFeatureFlags(state, payload) {
    state.featureFlag = payload
  },
  modifyCurrentStepNum(state, changeValue) {
    state.currentStepNum = state.currentStepNum + changeValue

    if (state.schema.verifications[state.currentStepNum]) {
      state.currentStepName =
        state.schema.verifications[state.currentStepNum].verificationCode
    } else {
      state.currentStepName = '--'
    }
  },
  setApplication(state, payload) {
    state.application[payload.key] = payload.value
    for (let index = 0; index < state.application.length; index++) {
      if (state.application[index].key === payload.key) {
        state.application.splice(index, 1)
      }
    }
    state.application.push(payload)

    state.application = [...state.application]
  },
  setCleanApplication(state, payload) {
    state.cleanApplication = payload
  },
  setUserPaymentOptions(state, payload) {
    state.userPaymentOptions = payload
  },
  setUserVendorConnections(state, payload) {
    state.userVendorConnections = payload
  },
  setUploadHelperDataMap(state, payload) {
    state.uploadHelperDataMap.set(
      payload.verificationCode,
      payload.uploadHelperData
    )
  },
  setCookie(state, payload) {
    this.$cookies.set(payload.name, payload.value, {
      path: '/',
      domain: process.env.COOKIE_DOMAIN,
      maxAge: 60 * 60 * 24 * 7,
    })

    Vue.set(state.cookies, payload.name, payload.value)
  },
  setVerificationTypes(state, payload) {
    state.allowedVerifications = payload
  },
  setModalState(state, payload) {
    state.modalState = payload
  },
  setVerficiations(state, payload) {
    state.verifications = payload
  },
  setTrigger(state, payload) {
    const payloadIndex = payload.index
    const trigger = payload.trigger
    const verificationCode = payload.verificationCode
    state.application.forEach((item, index) => {
      if (item.key === verificationCode) {
        item.value.submitTriggers[payloadIndex] = trigger
      }
    })
  },
  setComplianceRequirements(state, payload) {
    state.complianceRequirements = payload
  },
}

export const actions = {
  getImageCaptureFlag({ commit, state, rootState, ...rest }, fundEntityUuid) {
    commit('setLoading', true)
    this.$featureFlag
      .isEnabled([
        {
          name: 'ff_image_capture',
          extra_data: {
            fund_entity_uuid: [state.schema.fund_entity_uuid],
            entity_uuid: [rootState.fiiAuth.identity.entityUuid],
          },
        },
      ])
      .then((result) => {
        commit('setFeatureFlags', { ...result })
        commit('setLoading', false)
      })
      .catch((error) => {
        console.log(error)
        commit('setLoading', false)
      })
  },
  async getVerificationTypes(context) {
    /* eslint-disable no-unused-vars */
    try {
      const response = await this.$axios.$get(`/RELIEF_URL/verification/types`)
      console.log(response)
      context.commit('setVerificationTypes', response)
    } catch (error) {
      console.log(error)
    }
  },
  redirect(context, url) {
    consoleLogHeader(`redirecting in store to: ${url}`)

    this.$router.push({
      path: url,
    })
  },
  async getApplication(context, fundEntityUuid) {
    consoleLogHeader(`getApplication BEGIN:  ${fundEntityUuid}`)

    try {
      consoleLogHeaderItem('TOKEN BEING SENT:')
      consoleLogHeaderItem(context.state.token)
      const apiResponse = await this.$axios.$get(
        `/RELIEF_URL/application/${fundEntityUuid}`
      )
      consoleLogHeaderItem('STORE: getApplication Results success')
      consoleLogHeaderItem(apiResponse)
      // An application already exists that isn't declined
      return 'PENDING'
    } catch (error) {
      consoleLogHeaderItem('STORE: getApplication Results failure')
      if (error.response && error.response.status) {
        consoleLogHeaderItem(JSON.stringify(error, undefined, 2))
        switch (error.response.status) {
          case 401: // no authorization token
          case 422: // not enough segments -- social auth token was received instead of uptogether token
            // Not logged in
            return 'LOGIN'
          case 404:
            if (error.response.data.prev_declined === 0) {
              // No application exists yet.
              return 'OK'
            } else {
              // Previously declined app.
              return 'DECLINED'
            }
          case 409:
            // multiple applications exist.
            return 'MULTIPLE'
          default:
            // unknown error occured
            return 'UNKNOWN'
        }
      } else {
        // unknown error occured
        return 'UNKNOWN'
      }
    }
  },
  async modifyCurrentStepNum(context, changeValue) {
    consoleLogHeader('modifyCurrentStepNum')

    // check for final page.  Do not allow step increment
    // if we are unable to build a clean application
    const newStepNum = context.state.currentStepNum + changeValue
    const lastStep = context.state.schema.verifications.length
    if (newStepNum === lastStep) {
      // We are on the last page.  Attempt to build the application.  If there's
      // a failure, do not move forward.
      const success = await context.dispatch('buildApplication')
      if (!success) {
        alert('buildApplication failed!!')
      } else {
        context.commit('modifyCurrentStepNum', changeValue)
      }
    } else {
      // We are not on the last page (Confirmation page)
      context.commit('modifyCurrentStepNum', changeValue)

      consoleLogHeaderItem(
        `state.currentStepNum: ${context.state.currentStepNum}`
      )
      consoleLogHeaderItem(
        `schema.length: ${context.state.schema.verifications.length}`
      )

      let verificationCode = 'na'
      if (
        context.state.schema.verifications[context.state.currentStepNum] !==
        undefined
      ) {
        verificationCode =
          context.state.schema.verifications[context.state.currentStepNum]
            .verificationCode
      }

      const fundSlug = context.state.schema.fund_slug
      // Send url data for funnel logging
      this.$axios.$post(`/RELIEF_URL/application/progress`, {
        fund_entity_uuid: context.state.schema.fund_entity_uuid,
        step_name: verificationCode,
        step_num: context.state.currentStepNum,
        fund_slug: fundSlug,
      })
    }
  },
  setApplication(context, payload) {
    context.commit('setApplication', payload)
  },
  async loadSchemaForPartner(context, partnerSlug) {
    try {
      consoleLogHeader('loadSchemaForPartner')
      context.commit('setPartnerSchema', undefined)

      const apiResponse = await this.$axios.$get(
        `/RELIEF_URL/fund/slug/${partnerSlug}`
      )
      consoleLogHeaderItem('loadSchemaForPartner SUCCESS')

      if (
        apiResponse.fund_status &&
        ['ACTIVE', 'ACTIVE_TEST', 'CLOSED'].includes(
          apiResponse.fund_status.name
        )
      ) {
        context.commit('setPartnerSchema', apiResponse)
      } else {
        consoleLogHeaderItem(
          `fund status not ACTIVE.  It was: ${apiResponse.fund_status.name}`
        )
      }
    } catch (error) {
      // handle 404
      context.commit('setPartnerSchema', undefined)
    }
  },
  async executeSubmitTriggers(context, schema) {
    consoleLogHeader('executeSubmitTriggers')
    const submitTriggerEvidence = {}

    consoleLogHeaderItem(`${schema.submitTriggers.length} actions to execute`)
    consoleLogHeaderItem(JSON.stringify(schema.submitTriggers, undefined, 2))

    for (let index = 0; index < schema.submitTriggers.length; index++) {
      const trigger = JSON.parse(JSON.stringify(schema.submitTriggers[index]))
      consoleLogHeaderItem(`Executing: ${trigger.key}`)

      const artifact = await context.dispatch(
        trigger.key,
        schema.verificationCode
      )
      trigger.value = artifact
      const payload = {
        index,
        trigger,
        verificationCode: schema.verificationCode,
      }
      context.commit('setTrigger', payload)
      submitTriggerEvidence[trigger.key] = artifact

      // save schema with added artifact
      await context.dispatch('setApplication', {
        key: schema.verificationCode,
        value: schema,
      })
    }
    return submitTriggerEvidence
  },
  buildApplication(context) {
    consoleLogHeader('buildapplication')
    function clone(obj) {
      return JSON.parse(JSON.stringify(obj))
    }

    try {
      const v = context.state.application
      const cleanVerifications = []
      const extraCollectedData = []
      let confirmedInvitationCode
      let testOnly = false
      let payment
      let entityPaymentUuid

      const allowedPaymentTypes = [
        'STRIPE_DIRECT_DEPOSIT',
        'STRIPE_CARD_ISSUING_PHYSICAL',
        'STRIPE_CARD_ISSUING_DIGITAL',
      ]

      consoleLogHeaderItem('buildapplication: verifications begin')
      for (let i = 0; i < v?.length; i++) {
        if (v[i].key && v[i].key.toUpperCase() === 'PAYMENT') {
          consoleLogHeaderItem('buildapplication: PAYMENT begin')

          const submitTriggersArray = v[i].value.submitTriggers

          // Get either stripe or stripe2 value
          consoleLogHeaderItem('submitTriggersArray')
          /*
          {
            "key": "paymentsv2",
            "value": {
              "key": "paymentsv2",
              "value": {
                "entity_payment": {
                  "date_created": "2020-06-02T17:03:48.679725",
                  "entity_payment_uuid": "07a5e333-2f8b-404a-91c8-d9717e1382b9",
                  "entity_payment_vendor_product_uuid": "aff79498-a479-4a04-8408-4c55b027e3a9",
                  "meta": {
                    "card_fund_account_uuid": null,
                    "card_type": "physical"
                  },
                  "nickname": null,
                  "preferred": false,
                  "status": 1,
                  "value": "PLACEHOLDER"
                },
                "stripe_api_obj": {},
                "vendor_product": "Issuing"
              },
              "message": "ok"
            }
          }
*/
          const paymentPayloadValue = submitTriggersArray.find((x) =>
            ['stripe', 'stripe2', 'paymentsv2'].includes(x.key)
          ).value.value
          consoleLogHeaderItem('paymentPayloadValue')
          consoleLogHeaderItem(
            JSON.stringify(paymentPayloadValue, undefined, 2)
          )

          /* eslint-disable */
          entityPaymentUuid =
            paymentPayloadValue?.entity_payment?.entity_payment_uuid
          /* eslint-enable */

          if (entityPaymentUuid) {
            payment = paymentPayloadValue
          } else {
            consoleLogHeaderItem(paymentPayloadValue.type)
            // set payment here and use payment.type to show conditional template content
            if (allowedPaymentTypes.includes(paymentPayloadValue.type)) {
              payment = paymentPayloadValue
              continue
            } else {
              // ERROR, unsupported payment type
              consoleLogHeaderItem(
                `payment type is unsupported: ${paymentPayloadValue.type}.  Returning FALSE from buildApplication`
              )
              return false
            }
          }
        }

        if (v[i].key) {
          if (v[i].key.toUpperCase() === 'INVITE_CODE') {
            consoleLogHeaderItem('buildapplication: INVITE_CODE begin')
            consoleLogHeaderItem('INVITE_CODE submitTriggers: ' + v[i]?.value)
            const inviteVerificationSubmitTriggersArray =
              v[i]?.value?.submitTriggers
            // get invitationCode from submitTrigger
            const invite = inviteVerificationSubmitTriggersArray?.find(
              (x) => x.key === 'invitationCode'
            )
            consoleLogHeaderItem('INVITE_CODE value: ' + invite?.value?.message)
            if (
              invite?.value?.key === 'invitation_code' &&
              invite?.value?.message === 'ok'
            ) {
              confirmedInvitationCode = invite?.value?.value
              testOnly = !!invite?.value?.testOnly
            }
            continue
          }

          const verification = v[i]?.value

          // get fields for easy attach to evidence
          consoleLogHeaderItem('buildapplication: FIELDS begin')
          const cleanFields = {}

          consoleLogHeaderItem('FIELDS: ' + verification?.fields?.length)

          for (let j = 0; j < verification?.fields?.length; j++) {
            const field = verification.fields[j]
            cleanFields[field.field] = field.value
          }

          const cleanVerification = {
            verification_type: v[i].key,
            evidence: [],
          }

          consoleLogHeaderItem('buildapplication: EVIDENCE begin')
          // add evidence for... evidence
          for (let j = 0; j < verification?.evidence?.length; j++) {
            const evidence = verification.evidence[j]

            for (const property in evidence) {
              if (property.includes('_artifact_')) {
                const cleanEvidence = {
                  evidence_type:
                    evidence.type === 'UPLOAD' ? 'DOCUMENT' : evidence.type,
                  data: [],
                }

                const artifact = evidence[property]

                const dataJson = clone(cleanFields)
                dataJson.url = artifact.url
                dataJson.filename = artifact.filename
                dataJson.category = evidence.evidenceOptions.value
                dataJson.type = artifact.evidenceCode
                dataJson.subtype = artifact.type

                cleanEvidence.data.push(dataJson)
                cleanVerification.evidence.push(cleanEvidence)
                consoleLogHeaderItem('dataJson:  ' + dataJson)
                consoleLogHeaderItem('cleanEvidence:  ' + cleanEvidence)
              }
            }
          }

          // handle situation where there is no evidence, only fields
          if (verification?.evidence?.length === 0) {
            const cleanEvidence = {
              evidence_type: 'JSON',
              data: [],
            }
            const dataJson = clone(cleanFields)
            cleanEvidence.data.push(dataJson)
            cleanVerification.evidence.push(cleanEvidence)
          }

          consoleLogHeaderItem('buildapplication: TRIGGERS begin')
          // add evidence for triggers
          for (let j = 0; j < verification?.submitTriggers?.length; j++) {
            const evidence = verification.submitTriggers[j]

            const cleanEvidence = {
              evidence_type: 'JSON',
              data: [],
            }

            const dataJson = clone(cleanFields)
            dataJson.verification = evidence.value
            dataJson.action = evidence.key.toUpperCase()

            cleanEvidence.data.push(dataJson)

            cleanVerification.evidence.push(cleanEvidence)
          }

          console.log(
            'allowedVerifications: ',
            context.state.allowedVerifications
          )
          console.log('allowedVerifications vs v[i]: ', v[i])
          console.log('allowedVerifications vs v[i].key: ', v[i].key)
          if (context.state.allowedVerifications.includes(v[i].key)) {
            cleanVerifications.push(cleanVerification)
          } else {
            cleanVerification.fields = clone(cleanFields)
            extraCollectedData.push(cleanVerification)
          }
        }
      }
      // context.rootGetters['fiiAuth/getToken']('entity_uuid')

      const applicationObject = {
        token: context.rootState.fiiAuth.token,
        partner: context.state.schema.fund_slug,
        fund_entity_uuid: context.state.schema.fund_entity_uuid,
        title: context.state.schema.title.en,
        subtitle: context.state.schema.subtitle.en,
        title_es: context.state.schema.title.es,
        subtitle_es: context.state.schema.subtitle.es,
        paymentAmount: context.state.schema.payment_amount_per_recipient,
        invite_code: confirmedInvitationCode,
        testOnly,
        payment,
        verifications: cleanVerifications,
        extraCollectedData,
      }

      if (entityPaymentUuid) {
        applicationObject.entity_payment_uuid = entityPaymentUuid
      }

      /* eslint-disable */
      // Add auth group to application if it exists in the schema
      consoleLogHeaderItem(
        'apply_auth_group:' + context.state.schema?.apply_auth_group
      )
      if (context.state.schema?.apply_auth_group) {
        applicationObject.apply_auth_group =
          context.state.schema.apply_auth_group
      }

      // Add group to application if it exists in the schema
      consoleLogHeaderItem('apply_group:' + context.state.schema?.apply_group)
      if (context.state.schema?.apply_group) {
        applicationObject.apply_group = context.state.schema.apply_group
      }
      /* eslint-enable */

      // Temporarily add stripe_acct_id to root application object
      // until Issuing is finished.
      if (
        applicationObject.payment &&
        applicationObject.payment.type === 'STRIPE_DIRECT_DEPOSIT'
      ) {
        applicationObject.stripe_acct_id =
          applicationObject.payment.details.stripe_account_id
      }

      consoleLogHeaderItem('applicationObject')
      context.commit('setCleanApplication', applicationObject)

      if (!applicationObject) {
        consoleLogHeaderItem(
          'applicationObject is undefined.  Returning FALSE from buildApplication'
        )
        return false
      } else {
        consoleLogHeaderItem(
          'applicationObject is hydrated.  Returning TRUE from buildApplication'
        )
        return true
      }
    } catch (error) {
      consoleLogHeaderItem(error)
      consoleLogHeaderItem('buildApplication ERROR detected.  Returning FALSE')
      return false
    }
  },
  async submitApplication(context) {
    // save application to database
    consoleLogHeader('submitApplication')

    const payload = {
      fund_entity_uuid: context.state.cleanApplication.fund_entity_uuid,
      locale: this.$i18n.locale,
      program_name: context.state.cleanApplication.partner,
      application: context.state.cleanApplication,
    }
    try {
      consoleLogHeaderItem(payload.application)
      const apiResponse = await this.$axios.$put(
        `/RELIEF_URL/application`,
        payload
      )
      consoleLogHeaderItem(apiResponse)

      // refresh applications in dashboard
      await context.dispatch('applications/getApplications', {}, { root: true })

      return true
    } catch (error) {
      consoleLogHeaderItem(error)
      consoleLogHeaderItem(error.response)
      return false
    }
  },
  notYetImplemented(context) {
    Dialog.alert("This feature isn't ready yet.")
  },
  // Custom Submit Triggers
  async getChicagoWard(context, verificationCode) {
    consoleLogHeader('getChicagoWard')
    consoleLogHeaderItem(verificationCode)

    const app = context.getters.getApplicationArrayField(verificationCode)
    const geo = context.getters.getVerificationFieldValue(app, 'getGeo')
    consoleLogHeaderItem(geo)

    const payload = {
      lat: geo.lat_lon[0],
      lon: geo.lat_lon[1],
    }
    const returnItem = {
      key: 'chicago-ward',
      value: '',
    }
    try {
      const apiResponse = await this.$axios.$post(
        `/RELIEF_URL/chicago-ward`,
        payload
      )
      consoleLogHeaderItem(apiResponse)
      returnItem.value = apiResponse.ward_id
      return returnItem
    } catch (error) {
      returnItem.value = 'ERROR'
      return returnItem
    }
  },
  async getGeo(context, verificationCode) {
    try {
      consoleLogHeader('getGeo')
      const app = context.getters.getApplicationArrayField(verificationCode)

      const address1 = context.getters.getVerificationFieldValue(
        app,
        'address1'
      )
      const address2 = context.getters.getVerificationFieldValue(
        app,
        'address2'
      )
      const city = context.getters.getVerificationFieldValue(app, 'city')
      const state = context.getters.getVerificationFieldValue(app, 'state')
      const zip = context.getters.getVerificationFieldValue(app, 'zip')

      context.commit('setLoading', true)
      const geoResponse = await getGeo(this.$axios, {
        address1,
        address2,
        city,
        state,
        zip,
      })
      const cleanGeo = {
        address: geoResponse.geojson.features[0].properties.address,
        lat_lon: geoResponse.lat_lon,
      }
      context.commit('setLoading', false)
      return cleanGeo
    } catch (error) {
      consoleLogHeaderItem('getGeo: ' + error)
    } finally {
      context.commit('setLoading', false)
    }
  },
  stripe(context, verificationCode) {
    return {
      key: 'stripe',
      value: 'HALT',
      message:
        'This fund is updating its payment mechanism.  Please try again in a few minutes.',
    }
  },
  stripe2(context, verificationCode) {
    return {
      key: 'stripe2',
      value: 'HALT',
      message:
        'This fund is updating its payment mechanism.  Please try again in a few minutes.',
    }
  },
  async invitationCode(context, verificationCode) {
    consoleLogHeader('invitationCode')
    const app = context.getters.getApplicationArrayField(verificationCode)
    const code = context.getters.getVerificationFieldValue(
      app,
      'invitationCode'
    )
    try {
      const apiResponse = await this.$axios.$get(
        `/RELIEF_URL/invite_code/${context.state.schema.fund_entity_uuid}/${code}`
      )
      consoleLogHeaderItem('invitationCode SUCCESS')

      let returnValue = ''
      let returnMessage = ''

      if (apiResponse.is_consumed) {
        returnValue = 'HALT'
        returnMessage =
          'Oops! Looks like this code is not valid. Please try again with a different code.'
        // returnMessage = 'The given key has already been used.'
      } else if (
        apiResponse.invited_to_entity_uuid !==
        context.state.schema.fund_entity_uuid
      ) {
        returnValue = 'HALT'
        returnMessage =
          "Oops! Looks like something didn't work quite right. Please try your code again or contact the support team by emailing fund.support@uptogether.org"
        // returnMessage = 'The given key is invalid for this fund.'
      } else {
        returnValue = code
        returnMessage = 'ok'
      }

      return {
        key: 'invitation_code',
        value: returnValue,
        message: returnMessage,
        testOnly: apiResponse.test_only,
      }
    } catch (error) {
      consoleLogHeaderItem(error)
      consoleLogHeaderItem('invitation_code FAILURE')
      consoleLogHeaderItem(error.response)
      return {
        key: 'invitation_code',
        value: 'HALT',
        message:
          'Oops! Looks like this code is not valid. Please try again with a different code.',
        // message: 'Your invitation code is invalid.'
      }
    }
  },
  async berbixGetClientToken(context, verificationCode) {
    consoleLogHeader('berbixGetClientToken')
    context.commit('setBerbix', {
      ClientToken: undefined,
      Transaction: undefined,
      finished: false,
    })

    try {
      const apiResponse = await this.$axios.$get(
        '/RELIEF_URL/berbix/transaction'
      )
      consoleLogHeaderItem('berbixGetClientToken SUCCESS')
      const berbix = {
        ClientToken: apiResponse.berbix_client_token,
        Transaction: apiResponse.berbix_transaction,
        finished: false,
      }
      context.commit('setBerbix', berbix)
      return true
    } catch (error) {
      consoleLogHeaderItem(error)
      consoleLogHeaderItem('berbixGetClientToken FAILURE')
      consoleLogHeaderItem(error.response)
      return false
    }
  },
  commitmentClarity(context) {
    try {
      // eslint-disable-next-line dot-notation
      const verifications = context.state.application.filter(
        (x) => x.key === 'COMMITMENT_CLARITY'
      )
      if (!verifications.length === 0) {
        return {
          key: '"COMMITMENT"',
          value: false,
          message: 'Please check the commitment checkbox to continue',
        }
      } else {
        consoleLogHeader('commitmentClarity')

        const commitmentVerificationValue =
          verifications[0].value.evidence[0].value
        return {
          key: 'COMMITMENT',
          value: commitmentVerificationValue,
          message: 'ok',
        }
      }
    } catch (error) {
      console.log('Error at commitment clarity', error)
      return {
        key: 'COMMITMENT',
        value: false,
        message: 'Please check the commitment checkbox to continue',
      }
    }
  },
  async berbixGetResults(context) {
    consoleLogHeader('berbixGetResults')
    try {
      // wait a moment to allow berbix to process
      await new Promise((resolve, reject) => setTimeout(resolve, 1000))

      // perform berbix query
      const url = `/RELIEF_URL/berbix/transaction/${context.state.berbix.Transaction.transaction_uid}/results`
      consoleLogHeaderItem(url)

      const apiResponse = await this.$axios.$get(url)
      consoleLogHeaderItem('berbixGetResults SUCCESS')

      consoleLogHeaderItem(apiResponse)

      return {
        key: 'berbix',
        value: apiResponse,
        message: 'ok',
      }
    } catch (error) {
      consoleLogHeaderItem(error)
      consoleLogHeaderItem('berbixGetResults FAILURE')
      consoleLogHeaderItem(error.response)
      return {
        key: 'berbix',
        value: 'HALT',
        message: 'error.',
      }
    }
  },
  async regenerateGeocode({ rootState, rootGetters, dispatch }) {
    // Regenerate geocode for edited
    const { saveChanges = false } = rootState.dashboard.editAddress
    if (!saveChanges) {
      const primaryAddress = rootGetters['dashboard/primaryAddress']
      const address = await this.$axios.patch(
        `/PROFILE_URL/address/${primaryAddress.address_uuid}/regenerate-geocode`
      )

      await dispatch('dashboard/getAddresses', {}, { root: true })

      return address
    }
  },
  async addressv2(context) {
    consoleLogHeader('addressv2')
    try {
      // Add new check to regenerate geocode if the address wasn't edited
      await context.dispatch('regenerateGeocode')

      const primaryAddress = context.rootGetters['dashboard/primaryAddress']

      if (!primaryAddress || !primaryAddress.address_uuid) {
        return {
          key: 'addressv2',
          value: 'HALT',
          message: 'Please select your address.',
        }
      } else {
        return {
          key: 'addressv2',
          value: primaryAddress,
          message: 'ok',
        }
      }
    } catch (error) {
      consoleLogHeaderItem(error)
      consoleLogHeaderItem('addressv2 FAILURE')
      consoleLogHeaderItem(error.response)
      return {
        key: 'addressv2',
        value: 'HALT',
        message:
          'Please enter your address, then make sure to save before proceeding.',
      }
    }
  },
  async paymentsv2(context) {
    consoleLogHeader('paymentsv2')
    try {
      await new Promise((resolve, reject) => setTimeout(resolve, 10))

      const selectedPayment = context.rootState.dashboard.selectedPayment
      /* eslint-disable */
      if (!selectedPayment.entity_payment.entity_payment_uuid) {
        return {
          key: 'paymentsv2',
          value: 'HALT',
          message: 'Please select a payment method.',
        }
      } else {
        return {
          key: 'paymentsv2',
          value: selectedPayment,
          message: 'ok',
        }
      }
      /* eslint-enable */
    } catch (error) {
      consoleLogHeaderItem(error)
      consoleLogHeaderItem('paymentsv2 FAILURE')
      consoleLogHeaderItem(error.response)
      return {
        key: 'paymentsv2',
        value: 'HALT',
        message: 'Please select a payment method.',
      }
    }
  },
  async getComplianceRequirements(context, payload) {
    try {
      context.commit('setLoading', true)
      context.commit('setComplianceRequirements', [])
      const fundEntityUuid =
        payload.fundEntityUuid || context.state.schema.fund_entity_uuid
      const variables = {
        fundEntityUuid,
      }
      const query = await getGraphql('query-commitment-requirements.gql')
      const response = await this.$axios.$post('/GRAPHQL_URL/', {
        query,
        variables,
      })
      const data = response?.data?.me?.campaignsByCommitmentClarity || []
      context.commit('setLoading', false)
      context.commit('setComplianceRequirements', data)
      return data
    } catch (e) {
      context.commit('setLoading', false)
      context.commit('setComplianceRequirements', null)
      return null
    }
  },
}

export const getters = {
  navigationButtonsVisible: (state, getters, rootState, rootGetters) => {
    // do not show navigation buttons if user is actively
    // editing their address
    if (
      rootState.dashboard.editAddress.editing === true ||
      rootState.dashboard.editPayment.editing
    )
      return false

    return true
  },
  getCurrentStepName: (state) => () => {
    consoleLogHeaderItem('getCurrentStepName')
    return state.schema.verifications[state.currentStepNum].verificationCode
  },
  getApplicationArrayField: (state) => (fieldName) => {
    for (let index = 0; index < state.application.length; index++) {
      const element = state.application[index]
      if (element.key === fieldName) {
        return element.value
      }
    }
  },
  getVerificationFieldValue: (state) => (verification, field) => {
    // check fields
    for (let index = 0; index < verification.fields.length; index++) {
      const x = verification.fields[index]
      if (x.field === field) {
        return x.value
      }
    }

    // Check evidence
    for (let index = 0; index < verification.evidence.length; index++) {
      const x = verification.evidence[index]
      const evidenceType =
        field === 'invitationCode' ? 'INVITATION_CODE' : field

      if (x.type === evidenceType) {
        return x.value
      }
    }

    // check submitTriggers
    for (let index = 0; index < verification.submitTriggers.length; index++) {
      const x = verification.submitTriggers[index]
      if (x.key === field) {
        return x.value
      }
    }
  },
  getApplicationValue: (state) => (field) => {
    try {
      const appArray = state.application

      for (let i = 0; i < appArray.length; i++) {
        const verificationObject = appArray[i].value

        // check fields
        const snakeCaseField = snakeCase(field)

        for (let j = 0; j < verificationObject.fields.length; j++) {
          const x = verificationObject.fields[j]

          // For backward compatibility, we also compare it with the question code
          // The new version has the following format: question_code:question_hash
          // Adding ":" to the end works as a delimiter of the question code
          if (
            x.field.trim().toLowerCase() === field.trim().toLowerCase() ||
            x.field.trim().toLowerCase().includes(`${snakeCaseField}:`)
          ) {
            return x.value
          }
        }
      }
    } catch (error) {
      return undefined
    }

    return undefined
  },
  getPrimaryAddressValue: (state, getters, rootState) => (field) => {
    try {
      const primaryAddress = rootState.dashboard.addresses.find(
        (x) => x.address_type_id === 1
      )

      if (!primaryAddress) {
        return undefined
      }

      return primaryAddress?.[field]
    } catch (error) {
      return undefined
    }
  },
  getProfileValue: (state, getters, rootState) => (field) => {
    try {
      const identity = rootState.fiiAuth.identity
      console.log(`getProfileValue, identity: ${JSON.stringify(identity)}`)

      const profileProperty = getProfileAttributeFromField(field)
      const foundField = rootState?.fiiAuth?.identity?.meta?.[profileProperty]

      const isDate = function (date) {
        return new Date(date) !== 'Invalid Date' && !isNaN(new Date(date))
      }

      if (foundField && isDate(foundField)) {
        return foundField
      }

      if (foundField) {
        return foundField
      }

      return undefined
    } catch (error) {
      return undefined
    }
  },
}
